import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ImageIcon, Save, Loader2, ArrowUp, ArrowDown, LayoutGrid, Plus, X,
} from "lucide-react";

export default function HomepageAdmin() {
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/home-config").then(({ data }) => setCfg({ carousel: data.carousel || [], disciplines: data.disciplines || [] })).catch(() => setCfg({ carousel: [], disciplines: [] }));
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  const move = (list, idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return list;
    const next = [...list];
    [next[idx], next[to]] = [next[to], next[idx]];
    return next;
  };

  const setCarousel = (carousel) => setCfg((c) => ({ ...c, carousel }));
  const setDisciplines = (disciplines) => setCfg((c) => ({ ...c, disciplines }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/home-config", cfg);
      toast.success("Homepage updated. Changes are live.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="py-16 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const catBySlug = (s) => cats.find((c) => c.slug === s);
  const remaining = cats.filter((c) => !cfg.disciplines.includes(c.slug));
  const enabledCount = cfg.carousel.filter((x) => x.enabled !== false).length;

  return (
    <div data-testid="homepage-admin">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2"><ImageIcon className="w-6 h-6 text-amber-600" /> Homepage</h1>
          <p className="text-slate-500 text-sm">Reorder the hero carousel images and choose which disciplines appear on the home page.</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full gap-1.5 shrink-0" data-testid="homepage-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Carousel */}
        <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Hero carousel</h2>
          <p className="text-xs text-slate-400 mb-4">{enabledCount} of {cfg.carousel.length} images shown. Toggle, rename, and reorder them.</p>
          <div className="space-y-3">
            {cfg.carousel.map((item, idx) => (
              <div key={idx} data-testid={`carousel-item-${idx}`} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-2.5">
                <img src={item.src} alt={item.label} className="w-14 h-14 rounded-md object-cover shrink-0" />
                <div className="min-w-0 flex-1">
                  <Input value={item.label || ""} placeholder="Caption" onChange={(e) => setCarousel(cfg.carousel.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)))} className="h-8 text-sm" data-testid={`carousel-label-${idx}`} />
                  <div className="flex items-center gap-2 mt-1.5">
                    <Switch checked={item.enabled !== false} onCheckedChange={(v) => setCarousel(cfg.carousel.map((x, i) => (i === idx ? { ...x, enabled: v } : x)))} data-testid={`carousel-toggle-${idx}`} />
                    <span className="text-xs text-slate-500">{item.enabled !== false ? "Shown" : "Hidden"}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button type="button" onClick={() => setCarousel(move(cfg.carousel, idx, -1))} disabled={idx === 0} className="grid place-items-center w-7 h-7 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" data-testid={`carousel-up-${idx}`}><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setCarousel(move(cfg.carousel, idx, 1))} disabled={idx === cfg.carousel.length - 1} className="grid place-items-center w-7 h-7 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" data-testid={`carousel-down-${idx}`}><ArrowDown className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Disciplines */}
        <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-primary" /> Journals by Discipline</h2>
          <p className="text-xs text-slate-400 mb-4">Pick up to 6 disciplines in display order. Leave empty to show the first 6 automatically.</p>

          <div className="space-y-2 mb-4">
            {cfg.disciplines.length === 0 && <p className="text-sm text-slate-400 italic">Showing the first 6 disciplines automatically.</p>}
            {cfg.disciplines.map((slug, idx) => {
              const cat = catBySlug(slug);
              return (
                <div key={slug} data-testid={`discipline-item-${slug}`} className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2">
                  <span className="text-xs font-mono text-amber-600 w-5">{idx + 1}</span>
                  <span className="text-sm text-slate-800 dark:text-slate-200 flex-1 truncate">{cat?.name || slug}</span>
                  <button type="button" onClick={() => setDisciplines(move(cfg.disciplines, idx, -1))} disabled={idx === 0} className="grid place-items-center w-7 h-7 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" data-testid={`discipline-up-${slug}`}><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setDisciplines(move(cfg.disciplines, idx, 1))} disabled={idx === cfg.disciplines.length - 1} className="grid place-items-center w-7 h-7 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30" data-testid={`discipline-down-${slug}`}><ArrowDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setDisciplines(cfg.disciplines.filter((s) => s !== slug))} className="grid place-items-center w-7 h-7 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30" data-testid={`discipline-remove-${slug}`}><X className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>

          {cfg.disciplines.length < 6 && remaining.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {remaining.map((c) => (
                <button key={c.slug} type="button" onClick={() => setDisciplines([...cfg.disciplines, c.slug])} className="inline-flex items-center gap-1 text-xs rounded-full border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-slate-600 dark:text-slate-300 hover:border-primary hover:text-primary transition-colors" data-testid={`discipline-add-${c.slug}`}>
                  <Plus className="w-3 h-3" /> {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
